import { Box, Grid } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useAuth } from '../../session/InternalAuthProvider';
import { getOrderPage } from '../../common/AccountUtils';
import { AccountData, OrderData, OrderStatus } from '../../api';
import { getEmployeeNames } from '../CalendarUtils';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { OrderTopBar } from './OrderTopBar';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { CalendarBoxProvider, CalendarBoxTypography } from './CalendarBoxContext';

type OrderBoxProps = {
  order: OrderData;
  css?: any;
  viewType: 'month' | 'day';
  openOrder: (event: any, order: OrderData) => void;
  shouldNotDragNDrop?: boolean;
  hideEmployees?: boolean;
};

const OrderBox = ({ order, css, viewType, openOrder, shouldNotDragNDrop, hideEmployees }: OrderBoxProps) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: order.id,
  });

  const monthView = viewType === 'month';
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const orderPage = getOrderPage(account, order.order_page_id);
  const timeFormat = getTimeFormat(account || undefined);

  const { users, setShouldBlockSwiping } = useCalendar();
  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    setShouldBlockSwiping(isDragging);
  }, [isDragging, setShouldBlockSwiping]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const queryOrderId = urlParams.get('entityid');
    if (queryOrderId && queryOrderId === order.id) {
      setHighlight(true);
      setTimeout(() => {
        setHighlight(false);
        urlParams.delete('entityid');
        window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
      }, 3000); // Duration of the gradient animation
    }
  }, [order.id]);

  const hasNoEmployees = order.employee_ids.length === 0;

  const getTimeText = () => {
    if (!order.time) {
      return '';
    }
    return `${moment.unix(order.time).tz('utc').format('H:mm')} - ${moment
      .unix(order.time + order.duration)
      .tz('utc')
      .format(timeFormat)}`;
  };
  const formatEmployees = () => {
    if (order.employee_ids.length) {
      return getEmployeeNames(order.employee_ids, users).map((x) => (
        <Grid key={`employee-${x}`} item width="100%">
          <CalendarBoxTypography>{x}</CalendarBoxTypography>
        </Grid>
      ));
    }
    return '';
  };

  const shouldShowResources =
    account?.shared_resources?.scheduler_configs?.length && account?.shared_resources?.scheduler_configs?.length > 0;

  const shouldHaveRedBorder =
    !shouldShowResources && (hasNoEmployees || order.status === OrderStatus.PAYMENT_LINK_GENERATED);
  const is30MinOrder = order.duration <= 1800;
  const isMoreThen1HOrder = order.duration > 3600;

  // Create complete style objects for each view type
  const getCompleteStyles = () => {
    const baseStyles = {
      backgroundColor: orderPage?.color || '#6577b3',
      color: 'white',
      borderRadius: '8px',
      cursor: 'pointer',
      overflow: 'hidden',
      fontSize: '12px',
      border: shouldHaveRedBorder ? '1px solid red' : '1px solid rgb(230, 230, 230)',
      ...(highlight && {
        animation: 'gradient-animation 3s ease-in-out',
        '@keyframes gradient-animation': {
          '0%': {
            background: 'linear-gradient(45deg, #2b324c, #67ad5b)',
          },
        },
      }),
    };

    if (monthView) {
      return {
        ...baseStyles,
        marginLeft: '8px',
        marginRight: '8px',
        marginBottom: '2px',
        '@media (max-width: 600px)': {
          marginLeft: 0,
          marginRight: 0,
        },
      };
    }

    // Day view
    const dayBaseStyles = {
      ...baseStyles,
      position: 'absolute' as const,
      width: `calc(${css.width}% - 2px)`,
      left: `${css.left}%`,
      top: `${css.top}px`,
      height: `${css.height - 2}px`,
      transition: isDragging ? 'none' : 'transform 200ms ease',
      zIndex: isDragging ? 1000 : 'auto',
      ...(transform && { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }),
    };

    return dayBaseStyles;
  };

  // Get content items based on view type and size
  const getContentItems = () => {
    const items = [];

    // Time (for month view)
    if (monthView) {
      items.push(
        <Grid key="time" dir="ltr" sx={{ textAlign: 'center' }}>
          <CalendarBoxTypography>{getTimeText()}</CalendarBoxTypography>
        </Grid>,
      );
    }

    // Employees
    if (order.employee_ids.length > 0 && !hideEmployees) {
      items.push(
        <Grid
          width={'100%'}
          key="employees"
          container
          justifyContent="center"
          flexWrap="nowrap"
          alignItems="center"
          gap={0.5}
          sx={{ mt: is30MinOrder ? -0.5 : undefined }}>
          {formatEmployees()}
        </Grid>,
      );
    }
    if (is30MinOrder) {
      return items;
    }
    // Customer name
    const getMaxLines = () => {
      if (order.duration >= 7200) return 3; // 2+ hours: 3 lines
      if (order.duration > 3600) return 2; // 1+ hours: 2 lines
      return 1; // Less than 1 hour: 1 line
    };

    items.push(
      <Grid key="name" width={'100%'}>
        <CalendarBoxTypography
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: getMaxLines(),
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            whiteSpace: 'normal',
            textOverflow: 'unset',
          }}>
          {`${order.firstname} ${order.lastname}`}
        </CalendarBoxTypography>
      </Grid>,
    );

    // Order number

    items.push(
      <Grid key="order-number" sx={{ textAlign: 'center' }} mt={-0.5}>
        <CalendarBoxTypography>{`${order.order_number}#`}</CalendarBoxTypography>
      </Grid>,
    );

    return items;
  };

  // Unified content renderer with consistent shell
  const renderContent = () => {
    const contentItems = getContentItems();

    return (
      <Grid container flexDirection="column">
        <OrderTopBar order={order} orderPage={orderPage} />
        <Grid
          container
          flexDirection="column"
          px={0.5}
          alignContent={'center'}
          mt={!isMoreThen1HOrder ? -0.5 : undefined}>
          {contentItems}
        </Grid>
      </Grid>
    );
  };

  // Shared container with drag & drop and click handling
  const containerProps = monthView
    ? {}
    : {
        ref: shouldNotDragNDrop ? undefined : setNodeRef,
        ...(shouldNotDragNDrop ? {} : attributes),
        ...(shouldNotDragNDrop ? {} : listeners),
      };

  const handleClick = (event: any) => {
    if (monthView || !isDragging) {
      openOrder(event, order);
    }
  };

  const finalStyles = getCompleteStyles();

  return (
    <CalendarBoxProvider
      cssCalculatedWidth={css?.width}
      backgroundColor={orderPage?.color || '#6577b3'}
      viewType={viewType}>
      <Box {...containerProps} onClick={handleClick} sx={finalStyles}>
        <Grid
          container
          flexDirection="column"
          sx={{
            textAlign: 'center',
            wordWrap: 'break-word',
            height: '100%',
          }}>
          {renderContent()}
        </Grid>
      </Box>
    </CalendarBoxProvider>
  );
};

export default OrderBox;
